import { addCssToElement, once, wait } from '@mbrtargeting/metatag-utils';
import { MetaTag } from '../../loader/essentials/init-metatag.js';
import { logger } from '../../loader/essentials/logger.js';
import { LifecycleAction } from '../modules/adslot-controller/adslot-lifecycle-state.js';
import { Adslot } from '../modules/adslot-controller/adslot.js';
import { EffectFunction } from './index.js';
import { simpleStorageSwitch } from '../../utils/devops-options-helper.js';

const log = logger.logGroup({ prefix: 'Interstitial Effect' });

declare const window: Window & {
    SDG?: MetaTag;
};

/**
 * Creates close button for intersitial
 *
 * @param onclick
 */
const createCloseButton = (onclick: () => void): HTMLButtonElement => {
    const button = document.createElement('button');
    button.classList.add('sdgCloseButton');
    button.onclick = onclick;
    button.onkeydown = onclick; // necessary for screen readers to function properly
    button.setAttribute('aria-label', 'Close full screen ad break'); // add a description for visual impaired users
    return button;
};

const interstitialCss = `
    .sdgInterstitial {
        width:100%;
        height:100%;
        position:fixed;
        left:0;
        top:0;
        background-color:rgba(255,255,255,0.3);
        backdrop-filter:blur(5px);
        z-index:16000000;
    }
    .sdgInterstitial .sdgCloseButton {
        --sdg-close-button-size: clamp(10px, 1rem, 20px);
        --sdg-close-bar-size: calc(var(--sdg-close-button-size) * 2);
        padding:5px;
        margin:0 20px;
        width: var(--sdg-close-bar-size);
        height: var(--sdg-close-bar-size);
        background-size: var(--sdg-close-button-size);
        background-position: 50%;
        background-color: #fff;
        background-repeat: no-repeat;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg viewBox="341 8 13 13" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath fill="%234F4F4F" d="M354 9.31L352.69 8l-5.19 5.19L342.31 8 341 9.31l5.19 5.19-5.19 5.19 1.31 1.31 5.19-5.19 5.19 5.19 1.31-1.31-5.19-5.19z" fill-rule="evenodd"/%3E%3C/svg%3E');
    }
    .sdgInterstitial .sdgCloseButton:focus {
        outline: none;
    }
    .sdgInterstitial .sdgAnzeigenkennung {
        padding: 10px;
        display: inline-flex;
        justify-content: space-between;
        align-items:center;
        width: 100vw;
        background: #424242;
        color: white !important;
        font-size: 1.5rem !important;
    }
    .sdgInterstitial .sdgSlotHull {
        display: grid;
        align-items: baseline;
        justify-items: center;
        height: 100vh;
    }
    .sdgInterstitial .sdgAdServerAnchor { }
`;

//enables debugging and testing for support / development
const keepOpen: boolean = !!simpleStorageSwitch('sdgKeepInterstitialOpen', /^(1|true)$/);

/**
 * when slot is filled, start interstitial by appending closeButton and applying style class "sdgInterstitial".
 * Clicking the closeButton will unregister the adslot from the website (cleaning up this effect in same step)
 *
 * @param adslot {Adslot}
 */
export const INTERSTITIAL: EffectFunction = ({ slotName, containerNode, lifecycle, advertLabel }: Adslot) => {
    const onClose = once(() => {
        window.SDG!.Publisher.unregisterSlot(slotName);
    });

    const styleNode = addCssToElement(interstitialCss, containerNode);
    const closeButton = createCloseButton(onClose);

    // hide until adslot is filled by an actual ad, otherwise we bother user with an empty container
    containerNode.classList.add('sdgCloaked');

    // when ad is starting to render, show adslot
    lifecycle.onAction(LifecycleAction.FILL, async () => {
        log.debug('%s: setup effect.', [slotName.toUpperCase()]);
        advertLabel.appendChild(closeButton);
        containerNode.classList.add('sdgInterstitial');
        containerNode.classList.remove('sdgCloaked');
        // add attributes for visually impaired users
        containerNode.setAttribute('role', 'dialog');
        containerNode.setAttribute('aria-modal', 'true');
        // focus on the close button for visually impaired users
        closeButton.focus();
        // autoclose after 15 seconds, keep open when sessionStorage tells us
        if (keepOpen) return;
        wait(15000).then(onClose);
    }, { once: true });

    // cleanup function, executed on effect removal
    return () => {
        log.debug('%s: cleanup effect.', [slotName.toUpperCase()]);
        closeButton.remove();
        styleNode.remove();
        containerNode.classList.remove('sdgInterstitial', 'sdgCloaked');
        containerNode.removeAttribute('role');
        containerNode.removeAttribute('aria-modal');
    };
};
